import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Autocomplete,
  Collapse,
  Divider,
  alpha,
  useTheme,
} from '@mui/material'
import DevicesIcon from '@mui/icons-material/Devices'
import LayersIcon from '@mui/icons-material/Layers'
import SaveIcon from '@mui/icons-material/Save'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { getVendors } from 'store/lookup'
import { createDraft } from 'store/asset/asset-assign'
import * as snackbar from 'utils/snackbar'
import RequiredLabel from 'components/RequiredLabel'

const num = () => yup.number().transform((value, original) => (original === '' ? undefined : value))

const singleSchema = yup.object().shape({
  campusId: num().required('Required'),
  typeId: yup.string().required('Required'),
  vendorId: num().required('Required'),
  brand: yup.string().required('Required'),
  model: yup.string().required('Required'),
  price: num().positive().required('Required'),
  purchaseOrder: yup.string().required('Required'),
  purchaseDate: yup.date().required('Required'),
  assetNumber: yup.string().required('Required'),
  serialNumber: yup.string().required('Required'),
  life: num().positive().integer(),
})

const bulkSchema = yup.object().shape({
  campusId: num().required('Required'),
  typeId: yup.string().required('Required'),
  vendorId: num().required('Required'),
  brand: yup.string().required('Required'),
  model: yup.string().required('Required'),
  price: num().positive().required('Required'),
  purchaseOrder: yup.string(),
  purchaseDate: yup.date().required('Required'),
  life: num().positive().integer(),
  count: num().positive().integer().min(1).required('Required'),
})

const singleDefaults = {
  campusId: '',
  typeId: '',
  vendorId: '',
  brand: '',
  model: '',
  price: '',
  purchaseOrder: '',
  purchaseDate: dayjs(),
  assetNumber: '',
  serialNumber: '',
  life: 5,
}

const bulkDefaults = {
  campusId: '',
  typeId: '',
  vendorId: '',
  brand: '',
  model: '',
  price: '',
  purchaseOrder: '',
  purchaseDate: dayjs(),
  life: 5,
  count: 1,
}

function ModeCard({ selected, onClick, icon, title, description }) {
  const theme = useTheme()
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2.5,
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        userSelect: 'none',
        '&:hover': {
          borderColor: selected ? 'primary.main' : 'primary.light',
          bgcolor: alpha(theme.palette.primary.main, selected ? 0.08 : 0.03),
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: selected ? 'primary.main' : alpha(theme.palette.text.primary, 0.06),
          color: selected ? 'primary.contrastText' : 'text.secondary',
          flexShrink: 0,
          transition: 'all 0.15s ease',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body1" fontWeight={600} color={selected ? 'primary.main' : 'text.primary'}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
  )
}

export default function AddDeviceIndex() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [mode, setMode] = useState(null)

  const types = useSelector(state => state.lookup.types)
  const assetType = types?.filter(type => type.category === 'assetType') || []
  const vendors = useSelector(state => state.lookup.vendors) || []
  const campuses = useSelector(state => state.lookup.campus?.filter(c => ['provider', 'school'].includes(c.type)))

  useEffect(() => {
    if (!vendors || vendors.length === 0) {
      dispatch(getVendors())
    }
  }, [vendors, dispatch])


  const { control, handleSubmit, formState: singleFormState, reset } = useForm({
    resolver: yupResolver(singleSchema),
    defaultValues: singleDefaults,
  })

  const { control: bulkControl, handleSubmit: handleBulkSubmit, formState: bulkFormState, reset: resetBulk } = useForm({
    resolver: yupResolver(bulkSchema),
    defaultValues: bulkDefaults,
  })

  const onSubmitSingle = async data => {
    const res = await dispatch(createDraft(data))
    if (res.payload) {
      snackbar.success(t('Device added successfully'))
      reset()
    }
  }

  const onSubmitBulk = async data => {
    const { count, ...rest } = data
    const res = await dispatch(createDraft({ ...rest, count }))
    if (res.payload) {
      snackbar.success(t('{{count}} devices added successfully', { count }))
      resetBulk()
    }
  }

  const isSingle = mode === 'single'
  const isBulk = mode === 'bulk'
  const activeControl = isSingle ? control : bulkControl

  const CommonFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <Controller
          name="campusId"
          control={activeControl}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              value={campuses.find(o => o.id === field.value) ?? null}
              options={campuses}
              getOptionLabel={o => o.name || ''}
              renderInput={params => (
                <TextField
                  {...params}
                  label={<RequiredLabel>{t('Campus')}</RequiredLabel>}
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              onChange={(_, v) => field.onChange(v?.id || '')}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="typeId"
          control={activeControl}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              value={assetType.find(o => o.code === field.value) ?? null}
              options={assetType}
              getOptionLabel={o => o.name || ''}
              renderInput={params => (
                <TextField
                  {...params}
                  label={<RequiredLabel>{t('Type')}</RequiredLabel>}
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              onChange={(_, v) => field.onChange(v?.code || '')}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="vendorId"
          control={activeControl}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              value={vendors.find(o => o.id === field.value) ?? null}
              options={vendors}
              getOptionLabel={o => o.name || ''}
              renderInput={params => (
                <TextField
                  {...params}
                  label={<RequiredLabel>{t('Vendor')}</RequiredLabel>}
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              onChange={(_, v) => field.onChange(v?.id || '')}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="brand"
          control={activeControl}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={<RequiredLabel>{t('Brand')}</RequiredLabel>}
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="model"
          control={activeControl}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={<RequiredLabel>{t('Model')}</RequiredLabel>}
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={isSingle ? 3 : 6}>
        <Controller
          name="price"
          control={activeControl}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={<RequiredLabel>{t('Price')}</RequiredLabel>}
              type="number"
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={isSingle ? 3 : 6}>
        <Controller
          name="life"
          control={activeControl}
          render={({ field }) => (
            <TextField {...field} label={<RequiredLabel>{t('Life (years)')}</RequiredLabel>} type="number" size="small" fullWidth />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="purchaseOrder"
          control={activeControl}
          render={({ field }) => (
            <TextField {...field} label={<RequiredLabel>{t('Purchase Order')}</RequiredLabel>} size="small" fullWidth />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="purchaseDate"
          control={activeControl}
          render={({ field: { value, ...field }, fieldState }) => (
            <DatePicker
              {...field}
              value={dayjs(value)}
              label={<RequiredLabel>{t('Purchase Date')}</RequiredLabel>}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>

    </>
  )

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {t('Add Devices')}
      </Typography>

      <Paper elevation={0} >
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('How would you like to add devices?')}
        </Typography>

        <Box display="flex" gap={2}>
          <ModeCard
            selected={isSingle}
            onClick={() => setMode('single')}
            icon={<DevicesIcon fontSize="small" />}
            title={t('Single')}
            description={t('Add one device')}
          />
          <ModeCard
            selected={isBulk}
            onClick={() => setMode('bulk')}
            icon={<LayersIcon fontSize="small" />}
            title={t('Bulk')}
            description={t('Add multiple identical devices at once')}
          />
        </Box>

        <Collapse in={!!mode} timeout={250}>
          <Divider sx={{ my: 3 }} />

          {isSingle && (
            <Box>
              <Grid container spacing={2}>
                <CommonFields />
                <Grid item xs={12} md={6}>
                  <Controller
                    name="assetNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label={<RequiredLabel>{t('Asset Number')}</RequiredLabel>} size="small" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="serialNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label={<RequiredLabel>{t('Serial Number')}</RequiredLabel>} size="small" fullWidth />
                    )}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                <Button variant="outlined" onClick={() => reset()}>
                  {t('Reset')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!singleFormState.isDirty}
                  onClick={handleSubmit(onSubmitSingle)}
                >
                  {t('Add Device')}
                </Button>
              </Box>
            </Box>
          )}

          {isBulk && (
            <Box>
              <Grid container spacing={2}>
                <CommonFields />
                <Grid item xs={12} md={6}>
                  <Controller
                    name="count"
                    control={bulkControl}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label={<RequiredLabel>{t('Count')}</RequiredLabel>}
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ min: 1 }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                <Button variant="outlined" onClick={() => resetBulk()}>
                  {t('Reset')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!bulkFormState.isDirty}
                  onClick={handleBulkSubmit(onSubmitBulk)}
                >
                  {t('Add Devices')}
                </Button>
              </Box>
            </Box>
          )}
        </Collapse>
      </Paper>
    </Box>
  )
}
